<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import SelectPaletteDialog from "@/ui/components/colorPicker/SelectPaletteDialog.vue";
import { getColorsForPalette } from "@/ui/components/colorPicker/colorDefinitions";
import useDialog from "@/ui/composables/useDialog";
import { t, te } from "@/plugins/i18n";

const isVisible = defineModel<boolean>();

defineProps<{ isLoading: boolean }>();

const explorerStore = useExplorerStore();
const derivedDocument = computed(() => explorerStore.derivedDocument);
const document = computed(() => explorerStore.document);

const custom_colors = computed(
	() => derivedDocument.value?.custom_colors ?? []
);

watch(
	custom_colors,
	(colors) => {
		colors.forEach((e) => {
			if (e.is_checked_for_color_assignment === undefined) {
				e.is_checked_for_color_assignment = true;
			}
		});
	},
	{ immediate: true }
);

const sortedColors = computed(() =>
	[...custom_colors.value].sort(
		(a, b) => Number(b.visible_in_graph) - Number(a.visible_in_graph)
	)
);

const firstCheckedLegend = computed(
	() =>
		sortedColors.value.find((e) => e.is_checked_for_color_assignment)?.legend ??
		null
);

const showPaletteDialog = ref(false);
const useSelectedFirstColor = ref<null | string>(null);
const userSelectedFirstColorFromPaletteUuid = ref<null | string>(null);
const initialSnapshot = ref(JSON.stringify(custom_colors.value));
const mostRecentColorPaletteUuid = ref<null | string>(null);

const massAssignButtonLabel = computed(() =>
	useSelectedFirstColor.value ? "Continue Color Sequence" : "Randomize Colors"
);

const defineUserSelectedOptions = (paletteUuid: string, hexColor: string) => {
	userSelectedFirstColorFromPaletteUuid.value = paletteUuid;
	useSelectedFirstColor.value = hexColor;
};

const resetUserSelectedOptions = () => {
	userSelectedFirstColorFromPaletteUuid.value = null;
	useSelectedFirstColor.value = null;
};
const hasChanges = computed(
	() => JSON.stringify(custom_colors.value) !== initialSnapshot.value
);

const tryClose = async () => {
	if (hasChanges.value) {
		const confirmed = await useDialog().confirm({
			title: "Discard changes",
			message:
				"You have unsaved color changes. Are you sure you want to close without applying?",
		});
		if (!confirmed) return;
	}
	isVisible.value = false;
};

const removeInactive = () => {
	derivedDocument.value.custom_colors = custom_colors.value.filter(
		(e) => e.visible_in_graph
	);
};

const deleteEntry = (legend: string) => {
	derivedDocument.value.custom_colors = custom_colors.value.filter(
		(e) => e.legend !== legend
	);
};

const randomizeColors = async (uuid: string, startHexColor?: string) => {
	mostRecentColorPaletteUuid.value = uuid;

	const colors = await getColorsForPalette(uuid);
	if (!colors.length) return;
	const foundIndex = startHexColor ? colors.indexOf(startHexColor) : -1;
	let colorIndex =
		foundIndex !== -1 ? foundIndex : Math.floor(Math.random() * colors.length);
	custom_colors.value
		.filter((entry) => entry.is_checked_for_color_assignment)
		.forEach((entry) => {
			entry.color = colors[colorIndex];
			colorIndex = (colorIndex + 1) % colors.length;
		});
	resetUserSelectedOptions();
};

const applyColors = () => {
	document.value.custom_colors = custom_colors.value;
	document.value.used_color_palette_uuid = mostRecentColorPaletteUuid.value;

	initialSnapshot.value = JSON.stringify(custom_colors.value);
	isVisible.value = false;
};

const translateLegend = (legend: string) => {
	return te(`properties.${legend}.title`)
		? `${t(`properties.${legend}.title`)}`
		: legend;
};
</script>

<template>
	<v-dialog v-model="isVisible" persistent width="700">
		<v-card elevation="9">
			<v-toolbar flat>
				<v-toolbar-title>
					<i class="fa-light fa-paint-roller fa-lg mr-3" />
					Overwrite Graph Colors
				</v-toolbar-title>
				<v-spacer />
				<v-btn icon="mdi-close" @click="tryClose" />
			</v-toolbar>

			<v-divider />

			<v-alert
				class="ma-3"
				color="primary"
				:icon="false"
				style="overflow: visible"
			>
				<div class="d-flex align-center ga-4">
					<v-icon icon="fa: fa-light fa-message-check" />
					<div class="flex-grow-1">
						Enable this feature to customize legend colors. Changes apply only
						to this chart and won't affect other users.
					</div>
					<div class="pr-5">
						<v-switch
							v-model="document.is_color_customized"
							color="primary"
							hide-details
							density="compact"
						/>
					</div>
				</div>
			</v-alert>

			<v-progress-linear
				class="mx-3"
				indeterminate
				v-if="isLoading"
				height="2"
			/>

			<v-fade-transition>
				<div
					class="mx-3 mb-3"
					v-if="!isLoading && derivedDocument.is_color_customized"
				>
					<v-divider />

					<select-palette-dialog
						v-if="showPaletteDialog"
						v-model="showPaletteDialog"
						:initial-palette-uuid="userSelectedFirstColorFromPaletteUuid"
						:initial-hex-color="useSelectedFirstColor"
						@user-selects-palette="
							(uuid, startHexColor) => randomizeColors(uuid, startHexColor)
						"
					/>

					<div class="py-3 d-flex">
						<v-spacer />
						<v-btn
							prepend-icon="fa: fa-solid fa-fill-drip"
							variant="flat"
							color="secondary"
							@click="showPaletteDialog = true"
							>{{ massAssignButtonLabel }}</v-btn
						>
						<v-menu transition="slide-y-transition">
							<template v-slot:activator="{ props }">
								<v-btn
									class="ml-2"
									height="36"
									icon
									variant="text"
									size="small"
									color="on-surface"
									rounded="0"
									v-bind="props"
								>
									<v-icon icon="fa: fa-solid fa-ellipsis-vertical" />
								</v-btn>
							</template>
							<v-list density="compact">
								<v-list-item
									prepend-icon="fa: fa-light fa-broom"
									density="compact"
									@click="removeInactive"
								>
									<v-list-item-title class="text-caption"
										>Clear all inactive entries</v-list-item-title
									>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>

					<div class="color-row color-row--header pr-5 pl-2">
						<div class="checkbox-clip">
							<v-tooltip activator="parent">
								Checked values will be assigned a color when you click “{{
									massAssignButtonLabel
								}}”.
							</v-tooltip>
							<v-icon icon="fa: fa-light fa-circle-question" size="20" />
						</div>
						<span class="color-legend">Legend Value</span>
						<span class="color-header-color">Color</span>
					</div>

					<perfect-scrollbar
						style="overflow-x: hidden; max-height: 40vh"
						:options="{ suppressScrollX: true }"
					>
						<div
							v-for="entry in sortedColors"
							:key="entry.legend"
							class="color-row pr-5 pl-2"
							:class="{ 'color-row--inactive': !entry.visible_in_graph }"
						>
							<div class="checkbox-clip">
								<v-checkbox
									v-model="entry.is_checked_for_color_assignment"
									density="compact"
									hide-details
									color="primary"
								/>
							</div>
							<span class="color-legend">{{
								translateLegend(entry.legend)
							}}</span>
							<v-icon v-if="!entry.visible_in_graph" size="14" color="grey">
								mdi-eye-off-outline
							</v-icon>
							<color-picker
								v-model="entry.color"
								:on-color-click="
									entry.legend === firstCheckedLegend
										? defineUserSelectedOptions
										: null
								"
								:show-locked-onto-for-randomize="
									useSelectedFirstColor === entry.color &&
									entry.legend === firstCheckedLegend
								"
							/>
							<v-btn
								density="compact"
								variant="plain"
								icon="fa: fa-light fa-xmark"
								color="primary"
								@click="deleteEntry(entry.legend)"
							/>
						</div>
					</perfect-scrollbar>
				</div>
			</v-fade-transition>

			<v-divider />

			<v-card-actions class="mx-2 my-1">
				<v-spacer />
				<v-btn color="error" variant="text" @click="tryClose">Cancel</v-btn>
				<v-btn color="secondary" variant="flat" @click="applyColors"
					>Apply</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.color-row {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 6px 4px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	&:last-child {
		border-bottom: none;
	}

	&--inactive {
		opacity: 0.35;
	}
}

.color-row--header {
	border-bottom: 2px solid rgba(0, 0, 0, 0.12);
	padding-top: 0;
	padding-bottom: 4px;
	font-weight: 600;
}

.color-legend {
	font-size: 0.875rem;
	flex-grow: 1;
}

.color-header-color {
	font-size: 0.875rem;
	white-space: nowrap;
}

.checkbox-clip {
	width: 24px;
	height: 24px;
	overflow: hidden;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>
